/**
 * Leaderboard — client-side data loading + shared "Load More".
 *
 * The page HTML carries no DonorDrive data (see content-leaderboard.php), so
 * this fills each board from the cmn_leaderboard_load_more AJAX action once
 * the section nears the viewport (loader shown meanwhile) and appends
 * whenever the response arrives — a slow API never blocks the page, and a
 * full-page cache can't serve stale entries.
 *
 * Each .leaderboard-board is independent: it carries data-board (API type)
 * plus data-has-more / data-skip set from the server's responses. A failed
 * first load retries automatically, then falls back to a manual "Try again".
 * The single shared Load More button pages every board that still has rows.
 *
 * @package CMNBaseTheme
 */

/* global jQuery */

jQuery(document).ready(function ($) {
    'use strict';

    // Delays (ms) for automatic retries of a board's first load before the
    // manual "Try again" state is shown. The total (~37s) deliberately
    // outlasts the gateway's rate-limit penalty (it answers 429 "try again in
    // 27 seconds"), so a throttled board recovers on its own.
    const RETRY_DELAYS = [2000, 5000, 10000, 20000];

    $('.leaderboard').each(function () {
        const $root = $(this);
        const $list = $root.find('.leaderboard__list');

        if (!$list.length) {
            return;
        }

        const ajaxUrl = $list.data('ajax-url');
        const count = parseInt($list.data('count'), 10) || 5;
        const $boards = $list.find('.leaderboard-board');
        const $wrap = $root.find('.leaderboard__load-more-wrap');
        const $btn = $wrap.find('.leaderboard__load-more');

        const setState = ($board, state) => {
            $board.removeClass('is-loading is-error is-empty');

            if (state) {
                $board.addClass('is-' + state);
            }

            $board
                .find('.leaderboard-board__entries')
                .attr('aria-busy', state === 'loading');
        };

        const hasMore = ($board) => Boolean($board.data('has-more'));

        // Show the shared button while any board has another page.
        const syncLoadMore = () => {
            $wrap.toggleClass('is-visible', $boards.filter(function () {
                return hasMore($(this));
            }).length > 0);
        };

        const fetchPage = ($board, skip) =>
            $.ajax({
                url: ajaxUrl,
                type: 'GET',
                dataType: 'json',
                data: {
                    action: 'cmn_leaderboard_load_more',
                    board: $board.data('board'),
                    skip: skip,
                    count: count,
                },
            });

        const appendPage = ($board, response) => {
            $board
                .find('.leaderboard-board__entries')
                .append(response.data.html);
            $board
                .data('has-more', Boolean(response.data.has_more))
                .data('skip', response.data.next_skip);
        };

        const loadFirstPage = ($board, attempt = 0) => {
            setState($board, 'loading');

            const retry = () => {
                if (attempt < RETRY_DELAYS.length) {
                    window.setTimeout(
                        () => loadFirstPage($board, attempt + 1),
                        RETRY_DELAYS[attempt]
                    );
                } else {
                    setState($board, 'error');
                }
            };

            fetchPage($board, 0).then(function (response) {
                if (!response || !response.success) {
                    retry();
                    return;
                }

                appendPage($board, response);
                setState($board, response.data.html ? '' : 'empty');
                syncLoadMore();
            }, retry);
        };

        $list.on('click', '.leaderboard-board__retry', function () {
            loadFirstPage($(this).closest('.leaderboard-board'));
        });

        $btn.on('click', function () {
            if ($btn.prop('disabled')) {
                return;
            }

            const $active = $boards.filter(function () {
                return hasMore($(this));
            });

            if (!$active.length) {
                return;
            }

            $btn.prop('disabled', true).addClass('is-loading');

            // One request per board, in parallel, each from its own offset so
            // a failed board never skips rows when the button is clicked again.
            const requests = $active
                .map(function () {
                    const $board = $(this);

                    return fetchPage(
                        $board,
                        parseInt($board.data('skip'), 10) || 0
                    ).then(function (response) {
                        if (response && response.success) {
                            appendPage($board, response);
                        }
                    }, $.noop);
                })
                .get();

            $.when.apply($, requests).always(function () {
                $btn.prop('disabled', false).removeClass('is-loading');
                syncLoadMore();
            });
        });

        // Show the loader straight away, but only start fetching once the
        // section is about to scroll into view — visitors who never reach it
        // never hit the API. The margin starts the request a little early so
        // data is usually there by the time it's visible.
        $boards.each(function () {
            setState($(this), 'loading');
        });

        const loadAll = () =>
            $boards.each(function () {
                loadFirstPage($(this));
            });

        if (!('IntersectionObserver' in window)) {
            loadAll();
            return;
        }

        const observer = new window.IntersectionObserver(
            function (entries) {
                if (entries.some((entry) => entry.isIntersecting)) {
                    observer.disconnect();
                    loadAll();
                }
            },
            { rootMargin: '300px 0px' }
        );

        observer.observe($list[0]);
    });
});
